/**
 * Cart Drawer Component (Slice 6 / Issue #18)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * One web component, one responsibility. The cart drawer extends `Component`
 * directly — no shared drawer base, no inheritance chain to grok. Defaults are
 * hardcoded; if a future cart variant wants a different direction or close
 * threshold, fork this file (~150 lines) rather than wedge an attribute surface.
 *
 * BEHAVIOR (hardcoded — no `data-*` config surface)
 * ─────────────────────────────────────────────────────────────────────────────
 *   • Slide direction         right
 *   • Dismissible              yes — drag, escape, overlay click, close button
 *   • Drag-to-close threshold  25% of width or velocity > 0.4 px/ms
 *   • Focus trap               via Alpine `x-trap` in markup
 *   • Body scroll lock         set on open, cleared on close/disconnect
 *   • ARIA dialog              applied to [data-drawer-content]
 *
 * AUTO-OPEN POLICY
 * ─────────────────────────────────────────────────────────────────────────────
 * Subscribes to `cart:update` on `document` (via `this.signal` for cleanup).
 * Opens iff:
 *     event.detail.data.action === 'add'
 *  && event.detail.data.source?.addMode !== 'silent'
 *  && event.detail.data.source?.addMode !== 'page'
 *
 * Add modes are declared inline by triggers ("$cart.add({ id, mode })") and
 * pass through the engine to the event payload — see ADR-0008 and locked
 * decisions 11 / 14.
 *
 * UI REGISTRY
 * ─────────────────────────────────────────────────────────────────────────────
 * Self-registers with `$store.ui` in `connectedCallback`; unregisters in
 * `disconnectedCallback`. Registry reads truth from the `data-open` attribute
 * (Horizon pattern). `notifyStateChange` wakes Alpine effects on every
 * open/close — including drag-, overlay-, and escape-triggered closes.
 *
 * MARKUP CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 *   <cart-drawer id="cart-drawer" ...>
 *     <button data-drawer-overlay></button>
 *     <div data-drawer-content>
 *       <button data-drawer-close>×</button>
 *       ...
 *     </div>
 *   </cart-drawer>
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { Component } from './component-base.js';

// Event names from the single-source taxonomy (events.js IIFE).
const ThemeEvents = window.Theme?.events?.NAMES || {
  cartUpdate: 'cart:update',
};

// Drag tuning constants — hardcoded per Slice 6 decision (no `data-*` surface).
const DRAG_VELOCITY_THRESHOLD = 0.4; // px/ms — flick threshold
const DRAG_CLOSE_THRESHOLD = 0.25;   // % of drawer width — drag-distance threshold

export class CartDrawer extends Component {
  // Drag state
  #isDragging = false;
  #dragStart = null;
  #dragCurrent = null;
  #dragStartTime = null;

  // Focus restore
  #previousActiveElement = null;

  // Cached child elements
  #overlay = null;
  #content = null;

  connectedCallback() {
    super.connectedCallback();

    this.#overlay = this.querySelector('[data-drawer-overlay]');
    this.#content = this.querySelector('[data-drawer-content]');

    if (!this.#content) {
      this.log.warn('Missing [data-drawer-content] element');
      return;
    }

    this.#setupARIA();
    this.#setupDismissEvents();
    this.#setupDragEvents();
    this.#registerSurface();
    this.#subscribeToCartUpdate();

    // Bind Alpine directives (x-data / x-trap / x-show, incl. the upsell island)
    // inside the drawer. A custom element can upgrade EITHER before or after
    // Alpine.start(), so we must not assume Alpine is ready: calling initTree
    // before the stores and magics ($store.cart / $cart) are registered leaves
    // the subtree half-initialized and unresponsive, and Alpine won't re-init a
    // node it has already walked. So init now ONLY if Alpine has actually
    // started (cart store present); otherwise defer to `alpine:initialized`
    // (fired after the store registration + initial walk). Both paths are
    // idempotent — initTree skips already-initialized nodes.
    const initDrawerTree = () => {
      if (typeof Alpine !== 'undefined' && Alpine.initTree) Alpine.initTree(this);
    };
    if (typeof Alpine !== 'undefined' && Alpine.store && Alpine.store('cart')) {
      initDrawerTree();
    } else {
      document.addEventListener('alpine:initialized', initDrawerTree, { once: true, ...this.signal });
    }

    this.log.debug('Connected', { isOpen: this.isOpen });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.isOpen) {
      document.body.style.overflow = '';
    }
    this.#unregisterSurface();
    this.log.debug('Disconnected');
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /** Source of truth: the `data-open` attribute (Horizon pattern). */
  get isOpen() {
    return this.hasAttribute('data-open');
  }

  set isOpen(value) {
    value ? this.open() : this.close();
  }

  open() {
    if (this.isOpen) return;

    this.#previousActiveElement = document.activeElement;
    this.setAttribute('data-open', '');
    this.#content?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus the first focusable inside the dialog on the next frame so the
    // browser doesn't fight us during the opening transition.
    requestAnimationFrame(() => {
      const focusable = this.#content?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable ? focusable.focus() : this.#content?.focus();
    });

    this.#notifyRegistry();
    this.log.debug('Opened');
  }

  close() {
    if (!this.isOpen) return;

    this.removeAttribute('data-open');
    this.#content?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.#previousActiveElement?.focus?.();

    this.#notifyRegistry();
    this.log.debug('Closed');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // ─── Setup ───────────────────────────────────────────────────────────────

  #setupARIA() {
    this.#content.setAttribute('role', 'dialog');
    this.#content.setAttribute('aria-modal', 'true');
    if (!this.isOpen) {
      this.#content.setAttribute('aria-hidden', 'true');
    }
  }

  #setupDismissEvents() {
    const { signal } = this.signal;

    // Overlay click — soft dismiss
    this.#overlay?.addEventListener('click', () => this.close(), { signal });

    // Close buttons (any element marked [data-drawer-close])
    this.querySelectorAll('[data-drawer-close]').forEach((btn) => {
      btn.addEventListener(
        'click',
        (ev) => {
          ev.preventDefault();
          this.close();
        },
        { signal },
      );
    });

    // Escape key — global listener cleared on disconnect via signal
    document.addEventListener(
      'keydown',
      (ev) => {
        if (ev.key === 'Escape' && this.isOpen) {
          ev.preventDefault();
          this.close();
        }
      },
      { signal },
    );
  }

  /**
   * Right-direction-only drag math:
   *   - track pointer down on content
   *   - dampen left-drag (the "wrong" way) so the drawer doesn't fly off-screen
   *   - close on right-drag past threshold or fast flick
   *
   * Interactive controls inside the drawer don't initiate drag (so clicking
   * "checkout" or the upsell card doesn't accidentally drag-close). This
   * includes native controls AND ARIA widgets — critically the upsell card is
   * a `<div role="checkbox">`, so without `[role]` here a press on it would
   * start a drag + setPointerCapture and swallow the click (toggle never runs).
   */
  #setupDragEvents() {
    const { signal } = this.signal;

    this.#content.addEventListener(
      'pointerdown',
      (ev) => {
        if (ev.button !== 0) return;
        if (ev.target.closest('button, a, input, select, textarea, label, [role="checkbox"], [role="button"], [role="radio"], [role="switch"], [role="link"]')) return;
        this.#startDrag(ev);
      },
      { signal },
    );

    document.addEventListener('pointermove', (ev) => {
      if (!this.#isDragging) return;
      this.#onDrag(ev);
    }, { signal });

    document.addEventListener('pointerup', () => {
      if (!this.#isDragging) return;
      this.#endDrag();
    }, { signal });

    document.addEventListener('pointercancel', () => {
      if (!this.#isDragging) return;
      this.#endDrag();
    }, { signal });
  }

  #startDrag(ev) {
    this.#isDragging = true;
    this.#dragStart = { x: ev.clientX, y: ev.clientY };
    this.#dragCurrent = this.#dragStart;
    this.#dragStartTime = Date.now();

    this.#content.setPointerCapture?.(ev.pointerId);
    this.#content.style.transition = 'none';
  }

  #onDrag(ev) {
    this.#dragCurrent = { x: ev.clientX, y: ev.clientY };
    const dx = this.#dragCurrent.x - this.#dragStart.x;
    // Off-screen direction is +x (right). Left-drag is a no-op — drawer cannot
    // move toward the page center.
    const visualDx = Math.max(0, dx);
    this.#content.style.transform = `translateX(${visualDx}px)`;

    if (this.#overlay) {
      const progress = Math.min(1, visualDx / this.#getDrawerWidth());
      this.#overlay.style.opacity = String(1 - progress * 0.5);
    }
  }

  #endDrag() {
    const dx = this.#dragCurrent.x - this.#dragStart.x;
    const elapsed = Date.now() - this.#dragStartTime;
    const velocity = elapsed > 0 ? dx / elapsed : 0;
    const width = this.#getDrawerWidth();
    const progress = Math.abs(dx) / width;

    this.#isDragging = false;
    this.#content.style.transition = '';
    this.#content.style.transform = '';
    if (this.#overlay) this.#overlay.style.opacity = '';

    // Close on a right-direction flick OR a right-direction drag past threshold.
    // Guard: require at least 5px of movement to count as a drag at all.
    // Without this, a fast click can divide a 1px micro-movement by 1ms elapsed
    // and produce a "fake" 1 px/ms velocity that exceeds DRAG_VELOCITY_THRESHOLD.
    const closingRight = dx > 0;
    const MIN_DRAG_DX = 5;
    const realDrag = Math.abs(dx) >= MIN_DRAG_DX;
    const shouldClose =
      realDrag &&
      closingRight &&
      (velocity > DRAG_VELOCITY_THRESHOLD || progress > DRAG_CLOSE_THRESHOLD);

    if (shouldClose) this.close();
  }

  #getDrawerWidth() {
    return this.#content.getBoundingClientRect().width || 1;
  }

  // ─── $store.ui registration ─────────────────────────────────────────────

  /**
   * Self-register with the surface registry so `$store.ui.isOpen('cart-drawer')`,
   * `$store.ui.open('cart-drawer')`, etc. all work. `surface.isOpen` is a
   * getter against the DOM attribute — single source of truth.
   */
  #registerSurface() {
    if (!this.id) return;

    const doRegister = () => {
      if (typeof Alpine === 'undefined' || !Alpine.store) return;
      const ui = Alpine.store('ui');
      if (!ui || typeof ui.register !== 'function') return;

      ui.register(this.id, {
        type: 'drawer',
        element: this,
        get isOpen() {
          return this.element.hasAttribute('data-open');
        },
        open: () => this.open(),
        close: () => this.close(),
        meta: { closedAt: null, shownCount: 0 },
      });
    };

    if (typeof Alpine !== 'undefined' && Alpine.store) {
      doRegister();
    } else {
      document.addEventListener('alpine:init', doRegister, { once: true, ...this.signal });
    }
  }

  #unregisterSurface() {
    if (!this.id) return;
    if (typeof Alpine === 'undefined' || !Alpine.store) return;
    const ui = Alpine.store('ui');
    if (!ui || typeof ui.unregister !== 'function') return;
    ui.unregister(this.id);
  }

  /** Bump the registry tick so Alpine effects observing `isOpen()` re-run. */
  #notifyRegistry() {
    if (!this.id) return;
    if (typeof Alpine === 'undefined' || !Alpine.store) return;
    const ui = Alpine.store('ui');
    if (!ui || typeof ui.notifyStateChange !== 'function') return;
    ui.notifyStateChange(this.id);
  }

  // ─── cart:update subscription ──────────────────────────────────────────

  /**
   * Open on add unless the trigger declared a "non-opening" mode. This is the
   * locked auto-open policy (decision #14, #15). Listener uses `this.signal`
   * so it auto-cleans on disconnect.
   */
  #subscribeToCartUpdate() {
    document.addEventListener(
      ThemeEvents.cartUpdate,
      (e) => {
        const data = e.detail?.data;
        if (!data || data.action !== 'add') return;
        const mode = data.source?.addMode;
        if (mode === 'silent' || mode === 'page') return;
        this.log.debug('Auto-opening after add', { mode });
        this.open();
      },
      this.signal,
    );
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
